<template>
  <div>
    <h2>User Report</h2>

    <v-row align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedUsers"
          :items="userOptions"
          multiple
          searchable
          density="compact"
          label="Developer"
        >
          <template v-slot:prepend-item>
            <v-list-item
              title="Select All"
              @click="toggleSelectAllUsers"
            >
              <template v-slot:prepend>
                <v-checkbox-btn
                  :model-value="isAllUsersSelected"
                  :indeterminate="isSomeUsersSelected && !isAllUsersSelected"
                ></v-checkbox-btn>
              </template>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" :text="item.title"></v-chip>
            <span
              v-if="index === 2"
              class="text-grey text-caption align-self-center"
            >
              (+{{ selectedUsers.length - 2 }} others)
            </span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="startDate"
          type="date"
          clearable
          density="compact"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="endDate"
          label="End Date"
          type="date"
          clearable
          density="compact"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedEvent"
          :items="eventOptions"
          label="Scanning Event"
          density="compact"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedDevice"
          :items="deviceOptions"
          label="iOS Device"
          density="compact"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedBundleId"
          :items="bundleIdOptions"
          multiple
          searchable
          density="compact"
          label="Bundle ID"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" :text="item.raw"></v-chip>
            <span
              v-if="index === 2"
              class="text-grey text-caption align-self-center"
            >
              (+{{ selectedBundleId.length - 2 }} others)
            </span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedSdkVersion"
          :items="sdkVersionOptions"
          label="SDK Version"
          density="compact"
        ></v-select>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <v-tabs v-model="tab" bg-color="primary">
      <v-tab value="sensor-type">Sensor Type</v-tab>
      <v-tab value="structure-models">Structure Models</v-tab>
      <v-tab value="event-types">Event Types</v-tab>
      <v-tab value="td-vs-structure">TD vs Structure</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="sensor-type">
        <v-card>
          <v-card-title>Usage by Sensor Type by App</v-card-title>
          <v-data-table
            :headers="usageByAppHeaders"
            :items="usageByAppData"
            :loading="loadingUsageBySensorType"
          >
          </v-data-table>
        </v-card>
      </v-window-item>
      <v-window-item value="structure-models">
        <v-card>
          <v-card-title>Usage by App by Structure Sensor Model</v-card-title>
          <v-data-table
            :headers="usageByStructureSensorHeaders"
            :items="filteredUsageByStructureSensorData"
            :loading="loadingUsageByStructureSensor"
          >
          </v-data-table>
        </v-card>
      </v-window-item>
      <v-window-item value="event-types">
        <v-card>
          <v-card-title>Usage by App by Event Type</v-card-title>
          <v-data-table
            :headers="usageByEventTypeHeaders"
            :items="filteredUsageByEventTypeData"
            :loading="loadingUsageByEventType"
          >
          </v-data-table>
        </v-card>
      </v-window-item>
      <v-window-item value="td-vs-structure">
        <v-card>
          <v-card-title>Usage by Sensors (TD vs Structure)</v-card-title>
          <v-data-table
            :headers="usageByTdVStructureHeaders"
            :items="filteredUsageByTdVStructureData"
            :loading="loadingUsageByTdVStructure"
          >
          </v-data-table>
        </v-card>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';

export default {
  name: 'UserReport',
  setup() {
    const { getAccessTokenSilently } = useAuth0();
    const loadingUsageBySensorType = ref(false);
    const loadingUsageByStructureSensor = ref(false);
    const loadingUsageByEventType = ref(false);
    const loadingUsageByTdVStructure = ref(false);

    // User selection
    const userOptions = ref([]);
    const selectedUsers = ref([]);

    // Date range
    const startDate = ref(null);
    const endDate = ref(null);

    // Scanning Event
    const selectedEvent = ref('Both');
    const eventOptions = ['AccessToMesh', 'ExtractMesh', 'Both'];

    // iOS Device
    const selectedDevice = ref(null);
    const deviceOptions = ref([]); // Placeholder

    // Bundle ID
    const selectedBundleId = ref([]);
    const bundleIdOptions = ref([]);

    // SDK Version
    const selectedSdkVersion = ref(null);
    const sdkVersionOptions = ref([]); // Placeholder

    // Usage by App table
    const usageByAppHeaders = [
      { title: 'App Name', key: 'app_name' },
      { title: 'Bundle ID', key: 'bundle_identifier' },
      { title: 'Developer', key: 'developer_name' },
      { title: 'Structure', key: 'Structure' },
      { title: 'Apple Camera Front', key: 'AppleCameraFront' },
      { title: 'Apple LiDAR', key: 'AppleLiDAR' },
      { title: 'Apple TrueDepth', key: 'AppleTrueDepth' },
      { title: 'Apple Camera Back', key: 'AppleCameraBack' },
      { title: 'Apple Camera Back Ultra Wide', key: 'AppleCameraBackUltraWide' },
      { title: 'OCC', key: 'OCC' },
      { title: 'Other', key: 'Other_or_Unknown' },
      { title: 'Grand Total', key: 'grand_total_mesh_events' },
    ];
    const originalUsageByAppData = ref([]);

    const tab = ref('sensor-type');

    const usageByAppData = computed(() => {
      let filteredData = originalUsageByAppData.value;

      if (selectedUsers.value.length > 0) {
        filteredData = filteredData.filter(item => selectedUsers.value.includes(item.developer_name));
      }

      if (selectedBundleId.value.length > 0) {
        filteredData = filteredData.filter(item => selectedBundleId.value.includes(item.bundle_identifier));
      }

      return filteredData;
    });

    watch(tab, (newTab) => {
      if (newTab === 'sensor-type' && originalUsageByAppData.value.length === 0) {
        fetchUsageBySensorType();
      }
      if (newTab === 'structure-models' && originalUsageByStructureSensorData.value.length === 0) {
        fetchUsageByStructureSensor();
      }
      if (newTab === 'event-types' && originalUsageByEventTypeData.value.length === 0) {
        fetchUsageByEventType();
      }
      if (newTab === 'td-vs-structure' && originalUsageByTdVStructureData.value.length === 0) {
        fetchUsageByTdVStructure();
      }
    });

    watch([startDate, endDate, selectedEvent, selectedDevice, selectedSdkVersion], () => {
      if (tab.value === 'sensor-type') {
        fetchUsageBySensorType();
      }
      if (tab.value === 'structure-models') {
        fetchUsageByStructureSensor();
      }
      if (tab.value === 'event-types') {
        fetchUsageByEventType();
      }
      if (tab.value === 'td-vs-structure') {
        fetchUsageByTdVStructure();
      }
    });

    const usageByStructureSensorHeaders = ref([
      { title: 'App Name', key: 'app_name' },
      { title: 'Bundle ID', key: 'bundle_identifier' },
      { title: 'Developer', key: 'developer_name' },
      { title: 'ST01', key: 'ST01_scans' },
      { title: 'ST02A', key: 'ST02A_scans' },
      { title: 'ST02B', key: 'ST02B_scans' },
      { title: 'ST03', key: 'ST03_scans' },
      { title: 'Grand Total', key: 'grand_total_structure_scans' },
    ]);
    const originalUsageByStructureSensorData = ref([]);

    const filteredUsageByStructureSensorData = computed(() => {
      let filteredData = originalUsageByStructureSensorData.value;

      if (selectedUsers.value.length > 0) {
        filteredData = filteredData.filter(item => selectedUsers.value.includes(item.developer_name));
      }

      if (selectedBundleId.value.length > 0) {
        filteredData = filteredData.filter(item => selectedBundleId.value.includes(item.bundle_identifier));
      }

      return filteredData;
    });

    const usageByEventTypeHeaders = ref([
      { title: 'App Name', key: 'app_name' },
      { title: 'Bundle ID', key: 'bundle_identifier' },
      { title: 'Developer', key: 'developer_name' },
      { title: 'AccessToMesh Events', key: 'AccessToMesh_scans' },
      { title: 'ExtractMesh Events', key: 'ExtractMesh_scans' },
      { title: 'Grand Total', key: 'grand_total_mesh_events' },
    ]);
    const originalUsageByEventTypeData = ref([]);

    const filteredUsageByEventTypeData = computed(() => {
      let filteredData = originalUsageByEventTypeData.value;

      if (selectedUsers.value.length > 0) {
        filteredData = filteredData.filter(item => selectedUsers.value.includes(item.developer_name));
      }

      if (selectedBundleId.value.length > 0) {
        filteredData = filteredData.filter(item => selectedBundleId.value.includes(item.bundle_identifier));
      }

      return filteredData;
    });

    const usageByTdVStructureHeaders = ref([
      { title: 'App Name', key: 'app_name' },
      { title: 'Bundle ID', key: 'bundle_identifier' },
      { title: 'Developer', key: 'developer_name' },
      { title: 'Apple TrueDepth', key: 'AppleTrueDepth_scans' },
      { title: 'Structure Sensors', key: 'Structure_scans' },
      { title: 'Grand Total', key: 'grand_total_scans' },
    ]);
    const originalUsageByTdVStructureData = ref([]);

    const filteredUsageByTdVStructureData = computed(() => {
      let filteredData = originalUsageByTdVStructureData.value;

      if (selectedUsers.value.length > 0) {
        filteredData = filteredData.filter(item => selectedUsers.value.includes(item.developer_name));
      }

      if (selectedBundleId.value.length > 0) {
        filteredData = filteredData.filter(item => selectedBundleId.value.includes(item.bundle_identifier));
      }

      return filteredData;
    });

    // Usage by Device table
    const usageByDeviceHeaders = [
      { title: 'Developer', key: 'developer' },
      { title: 'App', key: 'app' },
      { title: 'SDK Version', key: 'sdkVersion' },
      { title: 'Bundle ID', key: 'bundleId' },
      { title: 'Host Device', key: 'hostDevice' },
      { title: 'TrueDepth Scans', key: 'trueDepthScans' },
      { title: 'Sensor Scans', key: 'sensorScans' },
      { title: 'Total Scans', key: 'totalScans' },
    ];
    const usageByDeviceData = ref([]);

    const fetchDevelopers = async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/developers`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        userOptions.value = data.map(user => ({ title: user.name, value: user.name }));
      } else {
        console.error('Failed to fetch developers');
      }
    };

    const fetchBundleIds = async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/apps/bundle_identifiers`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        bundleIdOptions.value = data.map(item => item.bundle_identifier);
      } else {
        console.error('Failed to fetch bundle ids');
      }
    };

    const fetchUsageBySensorType = async () => {
      loadingUsageBySensorType.value = true;
      try {
        const accessToken = await getAccessTokenSilently();
        const params = new URLSearchParams();
        if (startDate.value) {
          params.append('start_date', startDate.value);
        }
        if (endDate.value) {
          params.append('end_date', endDate.value);
        }
        if (selectedEvent.value) {
          params.append('event_type', selectedEvent.value);
        }

        if (selectedDevice.value && selectedDevice.value !== 'All') {
          params.append('device_identifier', selectedDevice.value);
        }
        if (selectedSdkVersion.value && selectedSdkVersion.value !== 'All') {
          params.append('sdk_version', selectedSdkVersion.value);
        }

        const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/scans/sensor?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        if (response.ok) {
          originalUsageByAppData.value = await response.json();
        } else {
          console.error('Failed to fetch usage by sensor type');
        }
      } catch (error) {
        console.error('Error fetching usage by sensor type:', error);
      } finally {
        loadingUsageBySensorType.value = false;
      }
    };

    const fetchUsageByStructureSensor = async () => {
      loadingUsageByStructureSensor.value = true;
      try {
        const accessToken = await getAccessTokenSilently();
        const params = new URLSearchParams();
        if (startDate.value) {
          params.append('start_date', startDate.value);
        }
        if (endDate.value) {
          params.append('end_date', endDate.value);
        }
        if (selectedEvent.value) {
          params.append('event_type', selectedEvent.value);
        }
        if (selectedDevice.value && selectedDevice.value !== 'All') {
          params.append('device_identifier', selectedDevice.value);
        }
        if (selectedSdkVersion.value && selectedSdkVersion.value !== 'All') {
          params.append('sdk_version', selectedSdkVersion.value);
        }

        const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/scans/structure_sensors?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        if (response.ok) {
          originalUsageByStructureSensorData.value = await response.json();
        } else {
          console.error('Failed to fetch usage by structure sensor');
        }
      } catch (error) {
        console.error('Error fetching usage by structure sensor:', error);
      } finally {
        loadingUsageByStructureSensor.value = false;
      }
    };

    const fetchUsageByEventType = async () => {
      loadingUsageByEventType.value = true;
      try {
        const accessToken = await getAccessTokenSilently();
        const params = new URLSearchParams();
        if (startDate.value) {
          params.append('start_date', startDate.value);
        }
        if (endDate.value) {
          params.append('end_date', endDate.value);
        }

        if (selectedDevice.value && selectedDevice.value !== 'All') {
          params.append('device_identifier', selectedDevice.value);
        }
        if (selectedSdkVersion.value && selectedSdkVersion.value !== 'All') {
          params.append('sdk_version', selectedSdkVersion.value);
        }

        const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/scans/events?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        if (response.ok) {
          originalUsageByEventTypeData.value = await response.json();
        } else {
          console.error('Failed to fetch usage by event type');
        }
      } catch (error) {
        console.error('Error fetching usage by event type:', error);
      } finally {
        loadingUsageByEventType.value = false;
      }
    };

    const fetchUsageByTdVStructure = async () => {
      loadingUsageByTdVStructure.value = true;
      try {
        const accessToken = await getAccessTokenSilently();
        const params = new URLSearchParams();
        if (startDate.value) {
          params.append('start_date', startDate.value);
        }
        if (endDate.value) {
          params.append('end_date', endDate.value);
        }
        if (selectedEvent.value) {
          params.append('event_type', selectedEvent.value);
        }

        if (selectedDevice.value && selectedDevice.value !== 'All') {
          params.append('device_identifier', selectedDevice.value);
        }
        if (selectedSdkVersion.value && selectedSdkVersion.value !== 'All') {
          params.append('sdk_version', selectedSdkVersion.value);
        }

        const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/scans/td_vs_structure?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        if (response.ok) {
          originalUsageByTdVStructureData.value = await response.json();
        } else {
          console.error('Failed to fetch usage by td vs structure');
        }
      } catch (error) {
        console.error('Error fetching usage by td vs structure:', error);
      } finally {
        loadingUsageByTdVStructure.value = false;
      }
    };

    const fetchDevices = async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/devices`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        deviceOptions.value = ['All', ...data.map(item => item.name)];
      } else {
        console.error('Failed to fetch devices');
      }
    };

    const fetchSdkVersions = async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/users/sdk_versions`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        sdkVersionOptions.value = ['All', ...data.map(item => item.sdk_version)];
      } else {
        console.error('Failed to fetch SDK versions');
      }
    };

    onMounted(() => {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate.value = firstDayOfMonth.toISOString().split('T')[0];
      endDate.value = today.toISOString().split('T')[0];

      fetchDevelopers();
      fetchBundleIds();
      fetchDevices();
      fetchSdkVersions();
      fetchUsageBySensorType();
    });

    const isAllUsersSelected = computed(() => selectedUsers.value.length === userOptions.value.length);
    const isSomeUsersSelected = computed(() => selectedUsers.value.length > 0);

    const toggleSelectAllUsers = () => {
      if (isAllUsersSelected.value) {
        selectedUsers.value = [];
      } else {
        selectedUsers.value = userOptions.value.map(user => user.value);
      }
    };

    return {
      selectedUsers,
      userOptions,
      startDate,
      endDate,
      selectedEvent,
      eventOptions,
      selectedDevice,
      deviceOptions,
      selectedBundleId,
      bundleIdOptions,
      selectedSdkVersion,
      sdkVersionOptions,
      usageByAppHeaders,
      usageByAppData,
      originalUsageByAppData,
      usageByStructureSensorHeaders,
      filteredUsageByStructureSensorData,
      originalUsageByStructureSensorData,
      usageByEventTypeHeaders,
      filteredUsageByEventTypeData,
      originalUsageByEventTypeData,
      usageByTdVStructureHeaders,
      filteredUsageByTdVStructureData,
      originalUsageByTdVStructureData,
      usageByDeviceHeaders,
      usageByDeviceData,
      isAllUsersSelected,
      isSomeUsersSelected,
      toggleSelectAllUsers,
      tab,
      loadingUsageBySensorType,
      loadingUsageByStructureSensor,
      loadingUsageByEventType,
      loadingUsageByTdVStructure,
    };
  },
};
</script>